import { useEffect, useRef } from 'react';

/**
 * Focus trapping, focus restoration and page scroll lock.
 *
 * Ports the shared machinery out of behaviour #7 (drawer) in
 * `behaviours/desktop-behaviours.reference.js` so Modal and Drawer cannot drift
 * apart. Everything a modal layer owes the user lives here:
 *
 *   Tab is trapped inside the panel, Escape closes, the page behind cannot
 *   scroll, the scrollbar gap is padded back so the layout does not jump, and
 *   focus RETURNS to whatever opened the panel.
 *
 * CSS side: `.dsk-scroll-locked` on <body>.
 */

/* Exactly the selector list the reference behaviours use. */
export const FOCUSABLE =
  'a[href],area[href],button:not([disabled]),input:not([disabled])' +
  ',select:not([disabled]),textarea:not([disabled]),iframe' +
  ',[tabindex]:not([tabindex="-1"])';

/**
 * Focusables that are actually on screen. A hidden element still matches the
 * selector, and trapping focus onto one strands the keyboard user somewhere
 * invisible.
 */
export function visibleFocusables(root) {
  if (!root) return [];
  return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), (el) => {
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
  });
}

/* Reference counted, so a popover opened from inside a drawer inside a modal
   cannot unlock the page while an outer layer is still open. */
let lockCount = 0;
let savedPadding = '';

export function lockPageScroll() {
  if (typeof document === 'undefined') return;
  lockCount += 1;
  if (lockCount > 1) return;

  /* Removing the scrollbar shifts the whole layout left by its width, which
     reads as the page jumping when the panel opens. Pad the gap back. */
  const gap = window.innerWidth - document.documentElement.clientWidth;
  savedPadding = document.body.style.paddingRight;
  if (gap > 0) document.body.style.paddingRight = `${gap}px`;
  document.body.classList.add('dsk-scroll-locked');
}

export function unlockPageScroll() {
  if (typeof document === 'undefined') return;
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;

  document.body.classList.remove('dsk-scroll-locked');
  document.body.style.paddingRight = savedPadding;
  savedPadding = '';
}

/**
 * The whole modal-layer contract in one hook.
 *
 * @param {object} options
 * @param {boolean} options.active         trap while true
 * @param {object}  options.panelRef       ref to the dialog element
 * @param {Function} options.onEscape      called on Escape, may be omitted
 * @param {object}  options.initialFocusRef optional element to focus first
 */
export function useModalLayer({ active, panelRef, onEscape, initialFocusRef }) {
  // Read the latest handler from a ref inside the effect so callers can pass an
  // inline arrow without retriggering it. Keying the effect on the handler
  // re-ran focus save/restore on every parent render, which ping-ponged focus
  // between the trigger and the panel (a visible flicker in the app's Modal).
  const onEscapeRef = useRef(onEscape);
  const initialFocusRefRef = useRef(initialFocusRef);
  useEffect(() => {
    onEscapeRef.current = onEscape;
    initialFocusRefRef.current = initialFocusRef;
  });

  useEffect(() => {
    if (!active) return undefined;

    const previouslyFocused = document.activeElement;
    lockPageScroll();

    /* [hidden] is display:none, so a panel revealed this tick has no layout to
       focus into until it is reflowed. A microtask-late focus is what the
       reference does with its forced reflow. */
    const focusTimer = setTimeout(() => {
      const wanted = initialFocusRefRef.current && initialFocusRefRef.current.current;
      if (wanted && typeof wanted.focus === 'function') {
        wanted.focus();
        return;
      }
      const first = visibleFocusables(panelRef.current)[0];
      if (first) first.focus();
      else if (panelRef.current) panelRef.current.focus();
    }, 0);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (onEscapeRef.current) {
          event.preventDefault();
          onEscapeRef.current();
        }
        return;
      }
      if (event.key !== 'Tab') return;

      /* Focus trap. Tab off either end wraps to the other, so focus can never
         land on the page behind a panel that claims aria-modal. */
      const node = panelRef.current;
      if (!node) return;
      const items = visibleFocusables(node);
      if (!items.length) {
        event.preventDefault();
        node.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement;
      if (event.shiftKey && (activeEl === first || !node.contains(activeEl))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (activeEl === last || !node.contains(activeEl))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown, true);
      unlockPageScroll();
      /* Focus goes back where it came from, so a keyboard user resumes at the
         control they were on rather than at the top of the document. */
      if (previouslyFocused && document.contains(previouslyFocused)) {
        if (typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
      }
    };
    // The panel is reached through its ref, so `active` is the only real dep.
    // eslint-disable-next-line
  }, [active]);
}

/** Merge a forwarded ref with a local one without losing either. */
export function mergeRefs(...refs) {
  return (node) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') ref(node);
      else ref.current = node;
    }
  };
}
